<template>
  <div
    :class="classes"
    :style="styles"
    @mouseleave="$_toggleArrowVisible(false)"
    @mouseover="$_toggleArrowVisible(true)"
  >
    <ol v-show="dots !== 'none'" :class="dotClasses">
      <li
        v-for="i in slides.length"
        :key="i"
        :class="[i - 1 === currentVal ? 'active': '']"
        @click="$_onDotsEvent('click', i - 1)"
        @mouseover="$_onDotsEvent('hover', i - 1)"
      >
        <span></span>
      </li>
    </ol>
    <div ref="carouselInner" :class="innerClasses" :style="innerStyle">
      <slot></slot>
    </div>
    <a
      v-show="arrowVisible"
      :class="`${prefixCls}-control-prev`"
      href="javascript:void(0)"
      role="button" data-slide="prev" @click="$_onArrowEvent(-1)"
    >
      <t-icon :class="`${prefixCls}-control-prev-icon`" :icon="vertical ? 'chevron-up-outline' : 'chevron-left-outline'"></t-icon>
      <span class="sr-only">Previous</span>
    </a>
    <a
      v-show="arrowVisible"
      :class="`${prefixCls}-control-next`"
      href="javascript:void(0)"
      role="button" data-slide="next" @click="$_onArrowEvent(1)"
    >
      <t-icon :class="`${prefixCls}-control-prev-icon`" :icon="vertical ? 'chevron-down-outline' : 'chevron-right-outline'"></t-icon>
      <span class="sr-only">Next</span>
    </a>
  </div>
</template>

<script>
import { PREFIX_CLASS } from '../../utils/constants'
import TIcon from '../icon'

const PREFIX_CLS = `${PREFIX_CLASS}carousel`
const EFFECT_SPEED = 300 // 过渡动画时长
export default {
  name: 'TCarousel',
  components: {
    TIcon
  },
  provide() {
    return {
      parentVM: this
    }
  },
  props: {
    arrow: {
      type: String,
      default: 'hover',
      validator(val) {
        return ['hover', 'always', 'never'].includes(val)
      }
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    },
    speed: {
      type: Number,
      default: 2000
    },
    easing: {
      type: String,
      default: 'ease'
    },
    dots: {
      type: String,
      default: 'inside',
      validator(val) {
        /* istanbul ignore next */
        return ['inside', 'none'].includes(val)
      }
    },
    trigger: {
      type: String,
      default: 'click',
      validator(val) {
        /* istanbul ignore next */
        return ['click', 'hover'].includes(val)
      }
    },
    value: {
      type: Number,
      default: 0
    },
    height: {
      type: [String, Number],
      default: 'auto',
      validator(val) {
        /* istanbul ignore next */
        return val === 'auto' || typeof val === 'number'
      }
    },
    effect: {
      type: String,
      default: '',
      validator(val) {
        /* istanbul ignore next */
        return ['fade', 'slide', ''].includes(val)
      }
    }
  },
  data() {
    return {
      currentVal: this.value,
      slides: [],
      ready: false,
      timer: null,
      slideEffectTimer: null,
      arrowVisible: this.arrow === 'always',
      prefixCls: PREFIX_CLS,
      transition: true,
      isAutoplay: this.autoplay
    }
  },
  computed: {
    classes() {
      return [
        PREFIX_CLS,
        {
          [`${PREFIX_CLS}--vertical`]: this.vertical,
          [`${PREFIX_CLS}--slide`]: this.effect === 'slide',
          [`${PREFIX_CLS}--fade`]: this.effect === 'fade',
          [`animate`]: this.transition
        }
      ]
    },
    styles() {
      return {
        height: `${this.height}px`
      }
    },
    dotClasses() {
      return `${PREFIX_CLS}-indicators`
    },
    innerClasses() {
      return `${PREFIX_CLS}-inner`
    },
    innerStyle() {
      if (this.effect === 'slide') {
        let activeIndex = this.currentVal
        let translate = this.vertical ? 'translateY' : 'translateX'
        let step = this.vertical ? this.height : 100
        let unit = this.vertical ? 'px' : '%'
        let move = activeIndex !== -1 ? (-(activeIndex + 1) * step) : 0
        return {
          transform: `${translate}(${move}${unit})`
        }
      } else {
        return {}
      }
    }
  },
  watch: {
    currentVal(newVal, oldVal) {
      this.$emit('change', newVal, oldVal)
      this.$_updateOffset()
    }
  },
  mounted() {
    this.$_resetTimer()
    if (this.effect === 'slide') { // 复制首尾dom节点用于slide动效
      this.$nextTick(() => {
        let reStr = `${PREFIX_CLS}-item`
        let cloneStr = `${PREFIX_CLS}-item ${PREFIX_CLS}-item-cloned`
        var firstChildHtml = this.$refs.carouselInner.children[0].outerHTML.replace(reStr, cloneStr)
        var lastChildHtml = this.$refs.carouselInner.children[this.$refs.carouselInner.children.length - 1].outerHTML.replace(reStr, cloneStr)
        let carouselInnerHtml = this.$refs.carouselInner.innerHTML
        this.$refs.carouselInner.innerHTML = lastChildHtml + carouselInnerHtml + firstChildHtml
      })
    }
  },
  methods: {
    addSlide(vm) {
      this.slides.push(vm)
      if (this.slides.length - 1 === this.currentVal) {
        vm.toggleActive(true)
      }
    },
    $_updateOffset() {
      this.slides.forEach((vm, i) => {
        if (i === this.currentVal) {
          vm.toggleActive(true)
        } else {
          vm.toggleActive(false)
        }
      })
    },
    $_toggleArrowVisible(visible) {
      this.arrow === 'hover' && (this.arrowVisible = visible)
      if (this.autoplay) {
        this.isAutoplay = !visible
        this.$_resetTimer()
      }
    },
    $_onDotsEvent(eventName, index) {
      if (eventName !== this.trigger) return
      if (this.currentVal !== index) {
        this.currentVal = index
        this.$emit('input', index)
        this.$_resetTimer()
      }
    },
    $_onArrowEvent(offset) {
      this.$_resetTimer()
      this.clearSlideEffectTimer()
      this.$_move(offset)
    },
    $_resetTimer() {
      this.timer && window.clearInterval(this.timer)
      if (this.isAutoplay && this.autoplay) {
        this.timer = setInterval(() => {
          this.$_move(1)
        }, this.speed)
      }
    },
    clearSlideEffectTimer() {
      clearTimeout(this.slideEffectTimer)
      this.slideEffectTimer = null
    },
    $_move(offset) {
      this.transition = true
      let index = this.currentVal
      let len = this.slides.length
      index += offset
      if (index === len) {
        // 当为slide效果状态下首尾节点时，先显示首尾副本，再快速显示首尾节点，用于无限循环效果
        if (this.effect === 'slide') {
          index = len
          setTimeout(() => {
            this.transition = false
            index = 0
            this.currentVal = index
            this.$emit('input', index)
          }, EFFECT_SPEED)
        } else {
          index = 0
        }
      }
      if (index < 0) {
        if (this.effect === 'slide') {
          index = -1
          setTimeout(() => {
            this.transition = false
            index = len - 1
            this.currentVal = index
            this.$emit('input', index)
          }, EFFECT_SPEED)
        } else {
          index = len - 1
        }
      }
      this.currentVal = index
      this.$emit('input', index)
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/module/carousel/carousel.vue